/**
 * Icon path utilities
 *
 * Standard: Store only the filename (e.g., "leetcode.png") in the database.
 * This utility handles path construction and backward compatibility with
 * records that may have full paths stored.
 */

const ICON_BASE_PATH = '/static/icons/'

/**
 * Converts an icon value from the database to a usable URL.
 * Handles both filename-only format and legacy full-path formats.
 *
 * @param iconValue - The icon value from the database (filename, path, or URL)
 * @returns Full URL path to the icon, or null if no icon
 *
 * @example
 * getIconUrl("leetcode.png") // => "/static/icons/leetcode.png"
 * getIconUrl("/icons/leetcode.png") // => "/icons/leetcode.png" (legacy)
 * getIconUrl("/static/icons/leetcode.png") // => "/static/icons/leetcode.png"
 * getIconUrl("https://cdn.example.com/icon.png") // => "https://cdn.example.com/icon.png"
 * getIconUrl(null) // => null
 */
export function getIconUrl(
  iconValue: string | null | undefined,
): string | null {
  if (!iconValue) return null

  // Already a full path or URL - use as-is (backward compatibility)
  if (iconValue.startsWith('/') || iconValue.startsWith('http')) {
    return iconValue
  }

  // Just a filename - prepend the base path
  return `${ICON_BASE_PATH}${iconValue}`
}

/**
 * Extracts just the filename from an icon path.
 * Used for normalizing database values to the standard format.
 *
 * @param iconValue - The icon value (may be filename, path, or URL)
 * @returns Just the filename, or null if no icon
 *
 * @example
 * getIconFilename("/icons/leetcode.png") // => "leetcode.png"
 * getIconFilename("/static/icons/leetcode.png") // => "leetcode.png"
 * getIconFilename("leetcode.png") // => "leetcode.png"
 * getIconFilename("https://cdn.example.com/icons/logo.png") // => "logo.png"
 * getIconFilename(null) // => null
 */
export function getIconFilename(
  iconValue: string | null | undefined,
): string | null {
  if (!iconValue) return null

  // Handle URLs and paths - extract just the filename
  if (iconValue.includes('/')) {
    const parts = iconValue.split('/')
    const filename = parts[parts.length - 1]
    // Return null if we end up with an empty string
    return filename || null
  }

  // Already just a filename
  return iconValue
}

/**
 * Matches a string made up entirely of pictographic emoji — including
 * variation selectors (⚙️), skin-tone modifiers, and ZWJ sequences (👩‍💻).
 *
 * `\p{Emoji}` is deliberately NOT used here. ASCII digits carry that Unicode
 * property, so `/^\p{Emoji}+$/u.test('123')` is true, and the two copies of
 * this logic that predate this component rendered a numeric filename as
 * literal text instead of loading the image.
 *
 * The escapes are spelled out (\uFE0F, \u200D) rather than pasted literally
 * because both characters are invisible in source.
 *
 * Trade-off: regional-indicator flags (🇺🇸) and keycaps (1️⃣, #️⃣) are
 * `Emoji=Yes` but `Extended_Pictographic=No`, so they now take the image
 * branch, 404, and fall back to the mark, whereas the old `\p{Emoji}` test
 * rendered them as literal text — deliberate, since the digit bug above is
 * worse than losing those two families to the fallback.
 */
const AUTHORED_EMOJI =
  /^(?:\p{Extended_Pictographic}(?:\p{Emoji_Modifier}|\uFE0F)?(?:\u200D\p{Extended_Pictographic}(?:\p{Emoji_Modifier}|\uFE0F)?)*)+$/u

export function isAuthoredEmoji(value: string): boolean {
  return AUTHORED_EMOJI.test(value)
}

/**
 * Whether an authored `icon` value will actually produce a mark.
 *
 * A value that is blank once trimmed counts as no icon at all — legacy rows
 * and hand-edits carry whitespace, and `AuthoredIcon` renders its fallback for
 * those. Anything deciding *whether* an icon exists has to normalise the same
 * way the component that renders it does, or the two disagree: a caller
 * testing raw truthiness sees `'   '` as an icon and asks for a mark that
 * comes back as a fallback. Kept beside the classification it guards so that
 * rule has one home.
 */
export function hasAuthoredIcon(iconValue: string | null | undefined): boolean {
  return (iconValue?.trim() ?? '') !== ''
}
